import React from 'react';
import { FiX, FiCheckCircle, FiClock, FiFileText, FiExternalLink } from 'react-icons/fi';
import './DoctorDetail.css';

export default function DoctorDetail({ doctor, guild, onClose, onEdit }) {
    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString('en-GB');
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content doctor-detail" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <div className="header-left">
                        <h2>Doctor Profile</h2>
                        <button
                            className="btn-edit-header"
                            onClick={() => onEdit(doctor.id)}
                            title="Edit Profile"
                        >
                            Edit Profile
                        </button>
                    </div>
                    <button className="close-button" onClick={onClose} title="Close">
                        <FiX size={24} />
                    </button>
                </div>

                <div className="modal-body">
                    {/* Profile Photo Section */}
                    <div className="profile-photo-section">
                        {doctor.profileImageUrl ? (
                            <img
                                src={doctor.profileImageUrl}
                                alt={doctor.fullName || 'Doctor'}
                                className="profile-photo-large"
                            />
                        ) : (
                            <div className="profile-photo-placeholder">
                                <span>{(doctor.fullName || doctor.email)?.[0]?.toUpperCase() || '?'}</span>
                            </div>
                        )}
                        <div className="profile-status">
                            <span className={`status-badge ${doctor.profileCompleted ? 'completed' : 'pending'}`}>
                                {doctor.profileCompleted ? (
                                    <>
                                        <FiCheckCircle />
                                        <span>Profile Completed</span>
                                    </>
                                ) : (
                                    <>
                                        <FiClock />
                                        <span>Profile Pending</span>
                                    </>
                                )}
                            </span>
                        </div>
                    </div>

                    {/* Basic Information */}
                    <div className="detail-section">
                        <h3>Basic Information</h3>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <label>Full Name</label>
                                <p>{doctor.fullName || 'Not provided'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Email</label>
                                <p>{doctor.email}</p>
                            </div>
                            <div className="detail-item">
                                <label>Phone</label>
                                <p>{doctor.phone || 'Not provided'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Date of Birth</label>
                                <p>{formatDate(doctor.dob)}</p>
                            </div>
                            <div className="detail-item">
                                <label>Gender</label>
                                <p>{doctor.gender || 'Not provided'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Guild</label>
                                <p>{guild?.name || 'Not assigned'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Membership Information */}
                    <div className="detail-section">
                        <h3>Membership Information</h3>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <label>Member Since</label>
                                <p>{formatDate(doctor.membershipStartDate)}</p>
                            </div>
                            <div className="detail-item">
                                <label>Membership Expires</label>
                                <p>{formatDate(doctor.membershipEndDate)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Professional Details */}
                    <div className="detail-section">
                        <h3>Professional Details</h3>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <label>Department</label>
                                <p>{doctor.departmentName || 'Not provided'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Specialty</label>
                                <p>{doctor.specialtyName || doctor.specialty || 'Not provided'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Medical Registration Number</label>
                                <p>{doctor.registrationNumber || 'Not provided'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Registration Authority</label>
                                <p>{doctor.registrationAuthority || 'Not provided'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Years of Experience</label>
                                <p>{doctor.experienceYears || '0'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Education */}
                    <div className="detail-section">
                        <h3>Education / Qualifications</h3>
                        <div className="education-list">
                            {doctor.education && doctor.education.length > 0 ? (
                                <ul className="unstyled-list">
                                    {doctor.education.map((edu, index) => (
                                        <li key={index} className="education-item">
                                            <FiCheckCircle size={14} className="accent-icon" />
                                            <span>{edu}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="no-data">No education details provided</p>
                            )}
                        </div>
                    </div>

                    {/* Workplace Details */}
                    <div className="detail-section">
                        <h3>Workplace Details</h3>
                        <div className="detail-grid">
                            <div className="detail-item full-width">
                                <label>Primary Hospital/Clinic</label>
                                <p>{doctor.primaryHospital || 'Not provided'}</p>
                            </div>
                            <div className="detail-item full-width">
                                <label>Primary Address</label>
                                <p>{doctor.primaryAddress || 'Not provided'}</p>
                            </div>
                            {doctor.secondaryHospital && (
                                <>
                                    <div className="detail-item full-width">
                                        <label>Secondary Hospital/Clinic</label>
                                        <p>{doctor.secondaryHospital}</p>
                                    </div>
                                    <div className="detail-item full-width">
                                        <label>Secondary Address</label>
                                        <p>{doctor.secondaryAddress || 'Not provided'}</p>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* About */}
                    {doctor.bio && (
                        <div className="detail-section">
                            <h3>About</h3>
                            <p className="bio-text">{doctor.bio}</p>
                        </div>
                    )}

                    {/* Documents */}
                    <div className="detail-section">
                        <h3>Documents</h3>
                        <div className="documents-grid">
                            {doctor.documentUrl ? (
                                <a
                                    href={doctor.documentUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="document-card"
                                >
                                    <div className="document-icon">
                                        <FiFileText size={32} />
                                    </div>
                                    <div className="document-info">
                                        <span className="document-title">Registration Document</span>
                                        <span className="document-action">
                                            <FiExternalLink size={16} />
                                            View Document
                                        </span>
                                    </div>
                                </a>
                            ) : (
                                <p className="no-documents">No documents uploaded</p>
                            )}
                        </div>
                    </div>

                    {/* System Information */}
                    <div className="detail-section system-info">
                        <h3>System Information</h3>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <label>Created At</label>
                                <p>{formatDate(doctor.createdAt)}</p>
                            </div>
                            <div className="detail-item">
                                <label>Last Updated</label>
                                <p>{formatDate(doctor.updatedAt)}</p>
                            </div>
                            <div className="detail-item">
                                <label>Password Changed</label>
                                <p>{doctor.passwordChanged ? 'Yes' : 'No'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
